import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { motion, AnimatePresence, useReducedMotion, useMotionValue, useTransform, animate, PanInfo } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Pair2Mark, RtaMark } from './PortalSelector';
import { Home as HomeIcon, Briefcase, User, Phone, MessageCircle, Sparkles, Navigation, ChevronLeft, ChevronRight, CheckCircle2, Clock, Star, Truck, MapPin, TrendingUp, ArrowRight, Signal, Wifi, Battery, Power, LogOut, Settings, CreditCard, Shield, Loader2, Camera, Plus, X, ImagePlus, Map as MapIcon } from 'lucide-react';
import { AskCustomerAiSlider } from '../components/AskCustomerAiSlider';
import { RoutePreviewMap } from '../components/RoutePreviewMap';
import { useAuth, signIn, signOut } from '../lib/authStore';
type DriverStatus = 'incoming' | 'accepted' | 'in_progress' | 'at_pickup' | 'picked' | 'at_dropoff' | 'completed';
type DriverJob = {
  id: string;
  service: string;
  customerName: string;
  customerPhone: string;
  pickup: string;
  dropoff: string;
  scheduled: string;
  price: number;
  status: DriverStatus;
  pickupPhotos?: string[];
  dropoffPhotos?: string[];
};
const LIFECYCLE: Array<{
  id: DriverStatus;
  label: string;
}> = [{
  id: 'accepted',
  label: 'Accepted'
}, {
  id: 'in_progress',
  label: 'In progress'
}, {
  id: 'at_pickup',
  label: 'At pickup'
}, {
  id: 'picked',
  label: 'Picked'
}, {
  id: 'at_dropoff',
  label: 'At dropoff'
}, {
  id: 'completed',
  label: 'Completed'
}];
function getNextStatus(s: DriverStatus): DriverStatus | null {
  const order: DriverStatus[] = ['incoming', 'accepted', 'in_progress', 'at_pickup', 'picked', 'at_dropoff', 'completed'];
  const idx = order.indexOf(s);
  if (idx === -1 || idx >= order.length - 1) return null;
  return order[idx + 1];
}
function getPrimaryCTA(s: DriverStatus): string {
  switch (s) {
    case 'incoming':
      return 'Accept job';
    case 'accepted':
      return 'Start trip';
    case 'in_progress':
      return "I'm at pickup";
    case 'at_pickup':
      return 'Picked up';
    case 'picked':
      return "I'm at dropoff";
    case 'at_dropoff':
      return 'Mark completed';
    default:
      return 'Done';
  }
}
function getStatusPill(s: DriverStatus): {
  label: string;
  className: string;
} {
  switch (s) {
    case 'incoming':
      return {
        label: 'New offer',
        className: 'bg-amber-50 text-amber-600 ring-1 ring-amber-200'
      };
    case 'accepted':
      return {
        label: 'Accepted',
        className: 'bg-blue-50 text-blue-600 ring-1 ring-blue-200'
      };
    case 'completed':
      return {
        label: 'Completed',
        className: 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200'
      };
    default:
      return {
        label: 'In progress',
        className: 'bg-brand-50 text-brand-600 ring-1 ring-brand-200'
      };
  }
}
const INITIAL_JOBS: DriverJob[] = [{
  id: 'JOB-2041',
  service: 'Apartment Move',
  customerName: 'Layla Hassan',
  customerPhone: '+971 50 234 7812',
  pickup: 'Downtown Dubai · Burj Views Tower 2',
  dropoff: 'Jumeirah Lakes Towers · Cluster G',
  scheduled: 'Today, 11:30 AM',
  price: 540,
  status: 'incoming'
}, {
  id: 'JOB-2038',
  service: 'Furniture Delivery',
  customerName: 'Omar Saleh',
  customerPhone: '+971 56 902 1144',
  pickup: 'IKEA Festival City',
  dropoff: 'Al Barsha South 3, Villa 22',
  scheduled: 'Today, 1:45 PM',
  price: 220,
  status: 'accepted'
}, {
  id: 'JOB-2033',
  service: 'Vehicle Recovery',
  customerName: 'Noor Al Awadi',
  customerPhone: '+971 55 778 4321',
  pickup: 'Sheikh Zayed Rd, Exit 41',
  dropoff: 'AAA Auto Service · Al Quoz 3',
  scheduled: 'Today, 9:10 AM',
  price: 310,
  status: 'in_progress'
}, {
  id: 'JOB-2028',
  service: 'Office Move',
  customerName: 'Faisal Mansour',
  customerPhone: '+971 52 901 0044',
  pickup: 'Business Bay · Bay Square 9',
  dropoff: 'DIFC Gate Village 4',
  scheduled: 'Yesterday',
  price: 880,
  status: 'completed'
}];
type AuthStage = 'intro' | 'splash' | 'phone' | 'otp';
export function DriverApp() {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [authStage, setAuthStage] = useState<AuthStage>('intro');
  const [pendingPhone, setPendingPhone] = useState('');
  const [tab, setTab] = useState<'home' | 'jobs' | 'profile'>('home');
  const [online, setOnline] = useState(true);
  const [jobs, setJobs] = useState<DriverJob[]>(INITIAL_JOBS);
  const [openJobId, setOpenJobId] = useState<string | null>(null);
  const [aiOpenFor, setAiOpenFor] = useState<string | null>(null);
  const activeJob = useMemo(() => jobs.find(j => ['accepted', 'in_progress', 'at_pickup', 'picked', 'at_dropoff'].includes(j.status)), [jobs]);
  const incoming = jobs.filter(j => j.status === 'incoming');
  const queue = jobs.filter(j => ['accepted', 'in_progress', 'at_pickup', 'picked', 'at_dropoff'].includes(j.status));
  const completed = jobs.filter(j => j.status === 'completed');
  const openJob = jobs.find(j => j.id === openJobId) || null;
  const aiJob = jobs.find(j => j.id === aiOpenFor) || null;
  const advance = (id: string, photos?: string[]) => {
    setJobs(prev => prev.map(j => {
      if (j.id !== id) return j;
      const next = getNextStatus(j.status);
      if (!next) return j;
      const updated: DriverJob = {
        ...j,
        status: next
      };
      if (photos && photos.length > 0) {
        if (j.status === 'at_pickup') {
          updated.pickupPhotos = [...(j.pickupPhotos || []), ...photos];
        } else if (j.status === 'at_dropoff') {
          updated.dropoffPhotos = [...(j.dropoffPhotos || []), ...photos];
        }
      }
      return updated;
    }));
  };
  const declineJob = (id: string) => {
    setJobs(prev => prev.filter(j => j.id !== id));
  };
  return <div className="min-h-screen bg-surface-100 flex flex-col items-center justify-center py-8 px-4 relative" data-id="element-1107">
      {/* Top chrome with branding */}
      <div className="absolute top-0 inset-x-0 px-4 py-4 flex items-center justify-between gap-4 z-10" data-id="element-1108">
        <button onClick={() => navigate('/')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white hover:bg-gray-50 ring-1 ring-gray-200 text-gray-700 text-xs font-bold transition-colors shadow-sm" data-id="element-1109">
          <ChevronLeft className="w-3.5 h-3.5" data-id="element-1110" />
          All modules
        </button>
        <div className="hidden sm:flex items-center gap-4 px-4 py-2 rounded-full bg-white/80 backdrop-blur-md ring-1 ring-gray-200 shadow-sm" data-id="element-1111">
          <Pair2Mark small data-id="element-1112" />
          <span className="w-px h-6 bg-gray-200" data-id="element-1113" />
          <RtaMark small data-id="element-1114" />
        </div>
      </div>

      <PhoneFrame data-id="element-1115">
        {!user ? <DriverSignInFlow stage={authStage} phone={pendingPhone} onSetStage={setAuthStage} onSetPhone={setPendingPhone} onComplete={p => {
        signIn(p, 'Driver');
        setAuthStage('intro');
      }} onGuest={() => {
        signIn('+971 50 555 0123', 'Driver');
        setAuthStage('intro');
      }} data-id="element-1116" /> : <>
            <div className="flex-1 overflow-y-auto scrollbar-hide bg-gradient-to-b from-white to-gray-50 text-gray-900" data-id="element-1117">
              <AnimatePresence mode="wait" data-id="element-1118">
                <motion.div key={tab} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.18
            }} data-id="element-1119">
                  {tab === 'home' && <HomeTab online={online} setOnline={setOnline} activeJob={activeJob || null} queueCount={queue.length} completedCount={completed.length} onOpenJob={setOpenJobId} data-id="element-1120" />}
                  {tab === 'jobs' && <JobsTab incoming={incoming} queue={queue} completed={completed} onAccept={advance} onDecline={declineJob} onOpenJob={setOpenJobId} data-id="element-1121" />}
                  {tab === 'profile' && <ProfileTab completedCount={completed.length} onSignOut={() => {
                signOut();
                setTab('home');
              }} data-id="element-1122" />}
                </motion.div>
              </AnimatePresence>
            </div>
            <BottomNav tab={tab} onChange={setTab} data-id="element-1123" />
          </>}
      </PhoneFrame>

      {/* Job detail overlay */}
      <AnimatePresence data-id="element-1124">
        {openJob && <JobDetailOverlay job={openJob} onClose={() => setOpenJobId(null)} onAdvance={photos => advance(openJob.id, photos)} onAskAi={() => setAiOpenFor(openJob.id)} data-id="element-1125" />}
      </AnimatePresence>

      {/* AI slider */}
      <AskCustomerAiSlider open={!!aiJob} customerName={aiJob?.customerName || 'Customer'} contextLabel={aiJob?.id} onClose={() => setAiOpenFor(null)} data-id="element-1126" />
    </div>;
}
function PhoneFrame({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="w-[375px] h-[812px] bg-white rounded-[3rem] shadow-2xl overflow-hidden border-[10px] border-gray-900 relative flex flex-col" data-id="element-1127">
      {/* Status bar */}
      <div className="h-11 w-full flex justify-between items-center px-7 pt-2 z-20 text-gray-900 text-[13px] font-semibold shrink-0 bg-white" data-id="element-1128">
        <span data-id="element-1129">9:41</span>
        <div className="w-32 h-7 bg-black rounded-full absolute left-1/2 -translate-x-1/2 top-2" data-id="element-1130"></div>
        <div className="flex items-center gap-1.5" data-id="element-1131">
          <Signal className="w-3.5 h-3.5" data-id="element-1132" />
          <Wifi className="w-3.5 h-3.5" data-id="element-1133" />
          <Battery className="w-4 h-4" data-id="element-1134" />
        </div>
      </div>
      {children}
    </div>;
}
function BottomNav({
  tab,
  onChange
}: {
  tab: 'home' | 'jobs' | 'profile';
  onChange: (t: 'home' | 'jobs' | 'profile') => void;
}) {
  const items: Array<{
    id: 'home' | 'jobs' | 'profile';
    label: string;
    icon: typeof HomeIcon;
  }> = [{
    id: 'home',
    label: 'Home',
    icon: HomeIcon
  }, {
    id: 'jobs',
    label: 'Jobs',
    icon: Briefcase
  }, {
    id: 'profile',
    label: 'Profile',
    icon: User
  }];
  return <div className="shrink-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 px-6 pt-2 pb-6 flex justify-around" data-id="element-1135">
      {items.map(it => {
      const Icon = it.icon;
      const active = tab === it.id;
      return <button key={it.id} onClick={() => onChange(it.id)} className="flex flex-col items-center gap-1 transition-colors" data-id="element-1136">
            <Icon className={`w-5 h-5 ${active ? 'text-brand-600' : 'text-gray-400'}`} data-id="element-1137" />
            <span className={`text-[10px] font-bold uppercase tracking-wider ${active ? 'text-brand-600' : 'text-gray-400'}`} data-id="element-1138">
              {it.label}
            </span>
          </button>;
    })}
    </div>;
}
function HomeTab({
  online,
  setOnline,
  activeJob,
  queueCount,
  completedCount,
  onOpenJob
}: {
  online: boolean;
  setOnline: (v: boolean) => void;
  activeJob: DriverJob | null;
  queueCount: number;
  completedCount: number;
  onOpenJob: (id: string) => void;
}) {
  return <div className="p-6 pb-8 space-y-6" data-id="element-1139">
      {/* Online toggle + greeting */}
      <div data-id="element-1140">
        <button onClick={() => setOnline(!online)} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${online ? 'bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200' : 'bg-gray-100 text-gray-500 ring-1 ring-gray-200'}`} data-id="element-1141">
          <span className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} data-id="element-1142" />
          {online ? 'Online · accepting jobs' : 'Offline'}
          <Power className="w-3 h-3" data-id="element-1143" />
        </button>
        <h1 className="text-2xl font-display font-bold mt-3 tracking-tight text-gray-900" data-id="element-1144">
          Good morning,
          <br data-id="element-1145" />
          <span className="orange-gradient-text" data-id="element-1146">Ahmed</span>
        </h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3" data-id="element-1147">
        <StatCard label="Today's earnings" value="AED 470" icon={TrendingUp} accent="emerald" data-id="element-1148" />
        <StatCard label="Jobs in queue" value={String(queueCount)} icon={Briefcase} accent="brand" data-id="element-1149" />
      </div>

      {/* Active job */}
      <div data-id="element-1150">
        <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-400 mb-3 px-1" data-id="element-1151">
          Active Job
        </h2>
        {activeJob ? <button onClick={() => onOpenJob(activeJob.id)} className="w-full text-left bg-white border border-gray-100 shadow-sm rounded-3xl p-5 active:scale-[0.99] transition-transform" data-id="element-1152">
            <div className="flex items-center justify-between mb-3" data-id="element-1153">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${getStatusPill(activeJob.status).className}`} data-id="element-1154">
                {getStatusPill(activeJob.status).label}
              </span>
              <span className="text-[10px] text-gray-400" data-id="element-1155">{activeJob.id}</span>
            </div>
            <h3 className="font-bold text-gray-900 text-base mb-1" data-id="element-1156">
              {activeJob.service}
            </h3>
            <p className="text-xs text-gray-500 mb-3" data-id="element-1157">
              {activeJob.customerName}
            </p>
            <div className="text-xs text-gray-600 flex items-center gap-1.5 mb-1 truncate" data-id="element-1158">
              <MapPin className="w-3 h-3 text-brand-500 shrink-0" data-id="element-1159" />
              <span className="truncate" data-id="element-1160">{activeJob.pickup}</span>
            </div>
            <div className="text-xs text-gray-600 flex items-center gap-1.5 truncate" data-id="element-1161">
              <MapPin className="w-3 h-3 text-emerald-500 shrink-0" data-id="element-1162" />
              <span className="truncate" data-id="element-1163">{activeJob.dropoff}</span>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between" data-id="element-1164">
              <span className="text-sm font-bold text-gray-900" data-id="element-1165">
                AED {activeJob.price}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-brand-600 font-bold" data-id="element-1166">
                {getPrimaryCTA(activeJob.status)}
                <ArrowRight className="w-3.5 h-3.5" data-id="element-1167" />
              </span>
            </div>
          </button> : <div className="bg-white border border-gray-100 shadow-sm rounded-3xl p-8 text-center text-gray-500 text-sm" data-id="element-1168">
            No active job — you're all caught up.
          </div>}
      </div>

      {/* Footer stat */}
      <div className="text-center text-xs text-gray-400" data-id="element-1169">
        {completedCount} jobs completed lifetime · ⭐ 4.92 rating
      </div>
    </div>;
}
function StatCard({
  label,
  value,
  icon: Icon,
  accent
}: {
  label: string;
  value: string;
  icon: typeof TrendingUp;
  accent: 'emerald' | 'brand';
}) {
  const accentMap = {
    emerald: 'text-emerald-600 bg-emerald-50',
    brand: 'text-brand-600 bg-brand-50'
  };
  return <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-4" data-id="element-1170">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${accentMap[accent]}`} data-id="element-1171">
        <Icon className="w-4 h-4" data-id="element-1172" />
      </div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-0.5" data-id="element-1173">
        {label}
      </p>
      <p className="text-lg font-display font-bold text-gray-900" data-id="element-1174">{value}</p>
    </div>;
}
function JobsTab({
  incoming,
  queue,
  completed,
  onAccept,
  onDecline,
  onOpenJob
}: {
  incoming: DriverJob[];
  queue: DriverJob[];
  completed: DriverJob[];
  onAccept: (id: string) => void;
  onDecline: (id: string) => void;
  onOpenJob: (id: string) => void;
}) {
  return <div className="p-6 pb-8 space-y-6" data-id="element-1175">
      <h1 className="text-2xl font-display font-bold tracking-tight text-gray-900" data-id="element-1176">
        Jobs
      </h1>

      {/* Incoming */}
      <section data-id="element-1177">
        <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-400 mb-3 flex items-center gap-2 px-1" data-id="element-1178">
          Incoming offers
          <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px]" data-id="element-1179">
            {incoming.length}
          </span>
        </h2>
        {incoming.length === 0 ? <p className="text-xs text-gray-500 px-1" data-id="element-1180">No new offers right now.</p> : <div className="space-y-3" data-id="element-1181">
            {incoming.map(j => <div key={j.id} className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 shadow-sm rounded-2xl p-4" data-id="element-1182">
                <JobCardBody job={j} onOpen={() => onOpenJob(j.id)} data-id="element-1183" />
                <div className="flex gap-2 mt-3" data-id="element-1184">
                  <button onClick={() => onDecline(j.id)} className="flex-1 py-2 rounded-lg bg-white text-gray-600 text-xs font-bold border border-gray-200 hover:bg-gray-50 transition-colors" data-id="element-1185">
                    Decline
                  </button>
                  <button onClick={() => onAccept(j.id)} className="flex-1 py-2 rounded-lg orange-gradient-bg text-white text-xs font-bold transition-transform active:scale-95" data-id="element-1186">
                    Accept
                  </button>
                </div>
              </div>)}
          </div>}
      </section>

      {/* Queue */}
      <section data-id="element-1187">
        <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-400 mb-3 px-1" data-id="element-1188">
          My queue
        </h2>
        {queue.length === 0 ? <p className="text-xs text-gray-500 px-1" data-id="element-1189">
            No active jobs in your queue.
          </p> : <div className="space-y-3" data-id="element-1190">
            {queue.map(j => <button key={j.id} onClick={() => onOpenJob(j.id)} className="w-full text-left bg-white border border-gray-100 shadow-sm rounded-2xl p-4 active:scale-[0.99] transition-transform" data-id="element-1191">
                <JobCardBody job={j} onOpen={() => onOpenJob(j.id)} data-id="element-1192" />
              </button>)}
          </div>}
      </section>

      {/* History */}
      <section data-id="element-1193">
        <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-400 mb-3 px-1" data-id="element-1194">
          Recent history
        </h2>
        {completed.length === 0 ? <p className="text-xs text-gray-500 px-1" data-id="element-1195">No completed jobs yet.</p> : <div className="space-y-2" data-id="element-1196">
            {completed.map(j => <button key={j.id} onClick={() => onOpenJob(j.id)} className="w-full text-left bg-white border border-gray-100 shadow-sm rounded-xl p-3 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-1197">
                <div className="min-w-0" data-id="element-1198">
                  <p className="text-sm font-bold text-gray-900 truncate" data-id="element-1199">
                    {j.service}
                  </p>
                  <p className="text-xs text-gray-500 truncate" data-id="element-1200">
                    {j.scheduled} · {j.customerName}
                  </p>
                </div>
                <div className="text-right shrink-0" data-id="element-1201">
                  <p className="text-sm font-bold text-emerald-600" data-id="element-1202">
                    AED {j.price}
                  </p>
                  <p className="text-[10px] text-gray-400" data-id="element-1203">{j.id}</p>
                </div>
              </button>)}
          </div>}
      </section>
    </div>;
}
function JobCardBody({
  job,
  onOpen
}: {
  job: DriverJob;
  onOpen: () => void;
}) {
  const pill = getStatusPill(job.status);
  return <div data-id="element-1204">
      <div className="flex items-center justify-between mb-2" data-id="element-1205">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${pill.className}`} data-id="element-1206">
          {pill.label}
        </span>
        <span className="text-[10px] text-gray-400" data-id="element-1207">{job.id}</span>
      </div>
      <h3 className="font-bold text-gray-900 text-sm mb-0.5" data-id="element-1208">{job.service}</h3>
      <p className="text-[11px] text-gray-500 mb-2" data-id="element-1209">{job.customerName}</p>
      <div className="text-xs text-gray-600 flex items-center gap-1.5 truncate mb-0.5" data-id="element-1210">
        <MapPin className="w-3 h-3 text-brand-500 shrink-0" data-id="element-1211" />
        <span className="truncate" data-id="element-1212">{job.pickup}</span>
      </div>
      <div className="text-xs text-gray-600 flex items-center gap-1.5 truncate mb-2" data-id="element-1213">
        <MapPin className="w-3 h-3 text-emerald-500 shrink-0" data-id="element-1214" />
        <span className="truncate" data-id="element-1215">{job.dropoff}</span>
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-gray-100" data-id="element-1216">
        <span className="text-[11px] text-gray-500 flex items-center gap-1" data-id="element-1217">
          <Clock className="w-3 h-3" data-id="element-1218" /> {job.scheduled}
        </span>
        <span className="text-sm font-bold text-gray-900" data-id="element-1219">AED {job.price}</span>
      </div>
    </div>;
}
function ProfileTab({
  completedCount,
  onSignOut
}: {
  completedCount: number;
  onSignOut: () => void;
}) {
  return <div className="p-6 pb-8 space-y-6" data-id="element-1220">
      <h1 className="text-2xl font-display font-bold tracking-tight text-gray-900" data-id="element-1221">
        Profile
      </h1>

      <div className="bg-white border border-gray-100 shadow-sm rounded-3xl p-5 flex items-center gap-4" data-id="element-1222">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-brand-400 to-amber-400 flex items-center justify-center font-bold text-xl text-white shadow-inner" data-id="element-1223">
          A
        </div>
        <div className="flex-1 min-w-0" data-id="element-1224">
          <p className="font-bold text-gray-900" data-id="element-1225">Ahmed Karim</p>
          <p className="text-xs text-gray-500 flex items-center gap-1" data-id="element-1226">
            <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-1227" /> 4.92 ·{' '}
            {completedCount + 312} reviews
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3" data-id="element-1228">
        <StatCard label="Lifetime earnings" value="AED 84.5K" icon={TrendingUp} accent="emerald" data-id="element-1229" />
        <StatCard label="Completed jobs" value={String(completedCount + 312)} icon={CheckCircle2} accent="brand" data-id="element-1230" />
      </div>

      <section data-id="element-1231">
        <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-400 mb-3 px-1" data-id="element-1232">
          Vehicle
        </h2>
        <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-4 flex items-center gap-3" data-id="element-1233">
          <div className="w-10 h-10 rounded-lg bg-brand-50 text-brand-600 flex items-center justify-center" data-id="element-1234">
            <Truck className="w-5 h-5" data-id="element-1235" />
          </div>
          <div className="flex-1 min-w-0" data-id="element-1236">
            <p className="font-bold text-gray-900 text-sm" data-id="element-1237">3-Ton Box Truck</p>
            <p className="text-xs text-gray-500" data-id="element-1238">
              D 84922 · Last serviced Aug 2
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-1" data-id="element-1239">
        <h2 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-400 mb-2 px-1" data-id="element-1240">
          Settings
        </h2>
        <SettingsRow icon={Shield} label="License & verification" data-id="element-1241" />
        <SettingsRow icon={CreditCard} label="Payment & payouts" data-id="element-1242" />
        <SettingsRow icon={Settings} label="Preferences" data-id="element-1243" />
      </section>

      <button onClick={onSignOut} className="w-full py-3.5 rounded-2xl bg-white border border-gray-200 hover:border-rose-200 hover:text-rose-600 text-gray-700 font-bold text-sm transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-1244">
        <LogOut className="w-4 h-4" data-id="element-1245" /> Sign out
      </button>
    </div>;
}
function SettingsRow({
  icon: Icon,
  label
}: {
  icon: typeof Shield;
  label: string;
}) {
  return <button className="w-full bg-white hover:bg-gray-50 border border-gray-100 shadow-sm rounded-xl p-3 flex items-center gap-3 transition-colors" data-id="element-1246">
      <div className="w-8 h-8 rounded-lg bg-gray-50 text-gray-600 flex items-center justify-center" data-id="element-1247">
        <Icon className="w-4 h-4" data-id="element-1248" />
      </div>
      <span className="text-sm text-gray-900 flex-1 text-left font-medium" data-id="element-1249">
        {label}
      </span>
      <ArrowRight className="w-4 h-4 text-gray-400" data-id="element-1250" />
    </button>;
}
function JobDetailOverlay({
  job,
  onClose,
  onAdvance,
  onAskAi
}: {
  job: DriverJob;
  onClose: () => void;
  onAdvance: (photos?: string[]) => void;
  onAskAi: () => void;
}) {
  const pill = getStatusPill(job.status);
  const currentIdx = LIFECYCLE.findIndex(s => s.id === job.status);
  const isCompleted = job.status === 'completed';
  const [photoSheet, setPhotoSheet] = useState<null | 'pickup' | 'dropoff'>(null);
  const [mapOpen, setMapOpen] = useState(false);
  const beforePicked = ['accepted', 'in_progress', 'at_pickup'].includes(job.status);
  const navStage: 'pickup' | 'dropoff' = beforePicked ? 'pickup' : 'dropoff';
  const waText = encodeURIComponent(`Hi ${job.customerName.split(' ')[0]}, this is your driver for ${job.service} (${job.id}). On my way to ${beforePicked ? job.pickup : job.dropoff}.`);
  const waHref = `https://wa.me/${job.customerPhone.replace(/\D/g, '')}?text=${waText}`;
  const telHref = `tel:${job.customerPhone.replace(/\s/g, '')}`;
  const handleSlideConfirm = () => {
    if (job.status === 'at_pickup') {
      setPhotoSheet('pickup');
    } else if (job.status === 'at_dropoff') {
      setPhotoSheet('dropoff');
    } else {
      onAdvance();
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1251">
      <div onClick={onClose} className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" data-id="element-1252" />
      <motion.div initial={{
      y: 30,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 30,
      opacity: 0
    }} transition={{
      type: 'spring',
      stiffness: 320,
      damping: 32
    }} className="relative w-[375px] h-[812px] bg-white rounded-[3rem] shadow-2xl overflow-hidden border-[10px] border-gray-900 flex flex-col" data-id="element-1253">
        <div className="shrink-0 bg-white px-5 pt-12 pb-4 border-b border-gray-100 shadow-sm z-10" data-id="element-1254">
          <div className="flex items-center justify-between mb-3" data-id="element-1255">
            <button onClick={onClose} className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Back" data-id="element-1256">
              <ChevronLeft className="w-5 h-5 text-gray-700" data-id="element-1257" />
            </button>
            <span className="text-[10px] text-gray-400" data-id="element-1258">{job.id}</span>
          </div>
          <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${pill.className}`} data-id="element-1259">
            {pill.label}
          </span>
          <h2 className="text-lg font-display font-bold text-gray-900 mt-2" data-id="element-1260">
            {job.service}
          </h2>
          <p className="text-xs text-gray-500" data-id="element-1261">
            {job.scheduled} · AED {job.price}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto bg-gray-50 p-5 space-y-5 scrollbar-hide" data-id="element-1262">
          {/* Customer */}
          <section data-id="element-1263">
            <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-400 mb-2 px-1" data-id="element-1264">
              Customer
            </h3>
            <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-4" data-id="element-1265">
              <p className="font-bold text-gray-900 text-sm" data-id="element-1266">
                {job.customerName}
              </p>
              <p className="text-xs text-gray-500" data-id="element-1267">{job.customerPhone}</p>
              <div className="grid grid-cols-3 gap-2 mt-3" data-id="element-1268">
                <a href={telHref} className="flex flex-col items-center gap-1 py-2 rounded-xl bg-emerald-50 hover:bg-emerald-100 ring-1 ring-emerald-200 transition-colors" data-id="element-1269">
                  <Phone className="w-4 h-4 text-emerald-600" data-id="element-1270" />
                  <span className="text-[10px] font-bold text-emerald-700" data-id="element-1271">
                    Call
                  </span>
                </a>
                <a href={waHref} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 py-2 rounded-xl bg-green-50 hover:bg-green-100 ring-1 ring-green-200 transition-colors" data-id="element-1272">
                  <MessageCircle className="w-4 h-4 text-green-600" data-id="element-1273" />
                  <span className="text-[10px] font-bold text-green-700" data-id="element-1274">
                    WhatsApp
                  </span>
                </a>
                <button onClick={onAskAi} className="flex flex-col items-center gap-1 py-2 rounded-xl bg-brand-50 hover:bg-brand-100 ring-1 ring-brand-200 transition-colors" data-id="element-1275">
                  <Sparkles className="w-4 h-4 text-brand-600" data-id="element-1276" />
                  <span className="text-[10px] font-bold text-brand-700" data-id="element-1277">
                    Ask AI
                  </span>
                </button>
              </div>
            </div>
          </section>

          {/* Route */}
          <section data-id="element-1278">
            <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-400 mb-2 px-1" data-id="element-1279">
              Route
            </h3>
            <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-4 space-y-3" data-id="element-1280">
              <div className="flex items-start gap-2" data-id="element-1281">
                <MapPin className="w-4 h-4 text-brand-500 mt-0.5 shrink-0" data-id="element-1282" />
                <div className="flex-1 min-w-0" data-id="element-1283">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-1284">
                    Pickup
                  </p>
                  <p className="text-sm text-gray-900" data-id="element-1285">{job.pickup}</p>
                </div>
              </div>
              <div className="flex items-start gap-2" data-id="element-1286">
                <MapPin className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" data-id="element-1287" />
                <div className="flex-1 min-w-0" data-id="element-1288">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-1289">
                    Dropoff
                  </p>
                  <p className="text-sm text-gray-900" data-id="element-1290">{job.dropoff}</p>
                </div>
              </div>
              <button onClick={() => setMapOpen(true)} className="w-full py-2.5 rounded-xl bg-gray-900 text-white font-bold text-xs flex items-center justify-center gap-1.5 hover:bg-gray-800 transition-colors shadow-md" data-id="element-1291">
                <MapIcon className="w-3.5 h-3.5" data-id="element-1292" />
                View on map
              </button>
            </div>
          </section>

          {/* Photos (if any) */}
          {(job.pickupPhotos?.length || job.dropoffPhotos?.length) && <section data-id="element-1293">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-400 mb-2 px-1" data-id="element-1294">
                Proof of handover
              </h3>
              <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-4 space-y-3" data-id="element-1295">
                {job.pickupPhotos?.length ? <div data-id="element-1296">
                    <p className="text-[10px] font-bold text-brand-600 uppercase tracking-wider mb-2" data-id="element-1297">
                      Pickup · {job.pickupPhotos.length} photo
                      {job.pickupPhotos.length === 1 ? '' : 's'}
                    </p>
                    <div className="grid grid-cols-4 gap-1.5" data-id="element-1298">
                      {job.pickupPhotos.map((src, i) => <div key={i} className="aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-100" data-id="element-1299">
                          <img src={src} alt={`Pickup photo ${i + 1}`} className="w-full h-full object-cover" data-id="element-1300" />
                        </div>)}
                    </div>
                  </div> : null}
                {job.dropoffPhotos?.length ? <div data-id="element-1301">
                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-2" data-id="element-1302">
                      Dropoff · {job.dropoffPhotos.length} photo
                      {job.dropoffPhotos.length === 1 ? '' : 's'}
                    </p>
                    <div className="grid grid-cols-4 gap-1.5" data-id="element-1303">
                      {job.dropoffPhotos.map((src, i) => <div key={i} className="aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-100" data-id="element-1304">
                          <img src={src} alt={`Dropoff photo ${i + 1}`} className="w-full h-full object-cover" data-id="element-1305" />
                        </div>)}
                    </div>
                  </div> : null}
              </div>
            </section>}

          {/* Lifecycle */}
          <section data-id="element-1306">
            <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-400 mb-2 px-1" data-id="element-1307">
              Lifecycle
            </h3>
            <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-4 space-y-3" data-id="element-1308">
              {LIFECYCLE.map((s, i) => {
              const reached = currentIdx >= i;
              const isCurrent = currentIdx === i;
              return <div key={s.id} className="flex items-center gap-3" data-id="element-1309">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${reached ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-400'} ${isCurrent ? 'ring-2 ring-brand-200 ring-offset-1' : ''}`} data-id="element-1310">
                      {reached ? <CheckCircle2 className="w-3 h-3" data-id="element-1311" /> : <span className="text-[10px] font-bold" data-id="element-1312">{i + 1}</span>}
                    </div>
                    <span className={`text-xs font-medium ${reached ? 'text-gray-900' : 'text-gray-400'}`} data-id="element-1313">
                      {s.label}
                    </span>
                  </div>;
            })}
            </div>
          </section>
        </div>

        {/* Sticky CTA — swipe slider */}
        {!isCompleted && <div className="shrink-0 bg-white border-t border-gray-100 p-5 pb-7 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] z-10" data-id="element-1314">
            <SwipeToConfirm label={getPrimaryCTA(job.status)} onConfirm={handleSlideConfirm} key={job.status} data-id="element-1315" />
          </div>}

        {/* In-app map screen */}
        <AnimatePresence data-id="element-1316">
          {mapOpen && <JobMapScreen job={job} navStage={navStage} onClose={() => setMapOpen(false)} data-id="element-1317" />}
        </AnimatePresence>

        {/* Photo capture sheet */}
        <AnimatePresence data-id="element-1318">
          {photoSheet && <PhotoCaptureSheet kind={photoSheet} onSkip={() => {
          setPhotoSheet(null);
          onAdvance();
        }} onConfirm={photos => {
          setPhotoSheet(null);
          onAdvance(photos);
        }} data-id="element-1319" />}
        </AnimatePresence>
      </motion.div>
    </motion.div>;
}
function SwipeToConfirm({
  label,
  onConfirm
}: {
  label: string;
  onConfirm: () => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const [trackWidth, setTrackWidth] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const measure = () => setTrackWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const THUMB = 52;
  const maxX = Math.max(0, trackWidth - THUMB - 4);
  const fillWidth = useTransform(x, v => v + THUMB);
  const labelOpacity = useTransform(x, [0, maxX * 0.6], [1, 0]);
  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (confirmed) return;
    if (x.get() > maxX * 0.85) {
      animate(x, maxX, {
        type: 'spring',
        stiffness: 360,
        damping: 30
      });
      setConfirmed(true);
      setTimeout(() => onConfirm(), 220);
    } else {
      animate(x, 0, {
        type: 'spring',
        stiffness: 360,
        damping: 30
      });
    }
  };
  return <div ref={trackRef} className="relative w-full h-14 rounded-2xl bg-gray-100 border border-gray-200 overflow-hidden select-none touch-none" data-id="element-1320">
      {/* Filled gradient track that grows with the thumb */}
      <motion.div style={{
      width: fillWidth
    }} className="absolute inset-y-0 left-0 orange-gradient-bg rounded-2xl will-change-transform" data-id="element-1321" />
      {/* Label */}
      <motion.div style={{
      opacity: labelOpacity
    }} className="absolute inset-0 flex items-center justify-center pointer-events-none text-sm font-bold text-gray-700" data-id="element-1322">
        <span className="ml-8" data-id="element-1323">{label}</span>
      </motion.div>
      {/* Confirmed overlay */}
      {confirmed && <div className="absolute inset-0 flex items-center justify-center text-sm font-bold text-white pointer-events-none" data-id="element-1324">
          <CheckCircle2 className="w-5 h-5 mr-1.5" data-id="element-1325" />
          Confirmed
        </div>}
      {/* Draggable thumb */}
      <motion.div drag={confirmed ? false : 'x'} dragConstraints={{
      left: 0,
      right: maxX
    }} dragElastic={0} dragMomentum={false} onDragEnd={handleDragEnd} style={{
      x
    }} whileTap={{
      scale: 0.96
    }} className="absolute top-1 left-1 w-[52px] h-[44px] rounded-xl bg-white shadow-md flex items-center justify-center text-brand-600 will-change-transform" data-id="element-1326">
        <ArrowRight className="w-5 h-5" data-id="element-1327" />
      </motion.div>
    </div>;
}
function PhotoCaptureSheet({
  kind,
  onSkip,
  onConfirm
}: {
  kind: 'pickup' | 'dropoff';
  onSkip: () => void;
  onConfirm: (photos: string[]) => void;
}) {
  const [photos, setPhotos] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const urls = Array.from(files).map(f => URL.createObjectURL(f));
    setPhotos(prev => [...prev, ...urls]);
  };
  const removePhoto = (idx: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
  };
  const title = kind === 'pickup' ? 'Confirm pickup' : 'Confirm dropoff';
  const helper = kind === 'pickup' ? 'Optionally add photos of the items at pickup. Helps avoid disputes later.' : 'Optionally add photos of the items delivered. Acts as proof of handover.';
  const tone = kind === 'pickup' ? 'text-brand-600 bg-brand-50 border-brand-200' : 'text-emerald-600 bg-emerald-50 border-emerald-200';
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18
  }} className="absolute inset-0 z-30 flex items-end" data-id="element-1328">
      <div className="absolute inset-0 bg-gray-900/40" onClick={onSkip} data-id="element-1329" />
      <motion.div initial={{
      y: 80
    }} animate={{
      y: 0
    }} exit={{
      y: 80
    }} transition={{
      type: 'spring',
      stiffness: 360,
      damping: 32
    }} className="relative w-full bg-white rounded-t-3xl shadow-2xl pt-3 pb-7 px-5" data-id="element-1330">
        <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" data-id="element-1331" />
        <div className="flex items-center justify-between mb-1" data-id="element-1332">
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${tone}`} data-id="element-1333">
            <Camera className="w-3 h-3" data-id="element-1334" />
            {kind}
          </span>
          <button onClick={onSkip} className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors" aria-label="Close" data-id="element-1335">
            <X className="w-3.5 h-3.5" data-id="element-1336" />
          </button>
        </div>
        <h3 className="text-lg font-display font-bold text-gray-900 mt-1" data-id="element-1337">
          {title}
        </h3>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed" data-id="element-1338">{helper}</p>
        {/* Photo grid */}
        <div className="mt-4 grid grid-cols-3 gap-2" data-id="element-1339">
          {photos.map((src, i) => <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-100" data-id="element-1340">
              <img src={src} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" data-id="element-1341" />
              <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center" aria-label="Remove photo" data-id="element-1342">
                <X className="w-3 h-3" data-id="element-1343" />
              </button>
            </div>)}
          {/* Add tile */}
          <button onClick={() => fileInputRef.current?.click()} className="aspect-square rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-brand-400 hover:text-brand-600 text-gray-500 flex flex-col items-center justify-center gap-1 transition-colors" data-id="element-1344">
            <ImagePlus className="w-5 h-5" data-id="element-1345" />
            <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-1346">
              {photos.length === 0 ? 'Add' : 'More'}
            </span>
          </button>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" multiple capture="environment" onChange={e => {
        handleFiles(e.target.files);
        e.target.value = '';
      }} className="hidden" data-id="element-1347" />
        <p className="text-[10px] text-gray-400 mt-3 text-center" data-id="element-1348">
          Photos are optional · {photos.length} added
        </p>
        <div className="mt-4 grid grid-cols-2 gap-2" data-id="element-1349">
          <button onClick={onSkip} className="py-3.5 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-sm transition-colors" data-id="element-1350">
            Skip
          </button>
          <button onClick={() => onConfirm(photos)} className="py-3.5 rounded-2xl orange-gradient-bg text-white font-bold text-sm shadow-lg shadow-brand-500/30 active:scale-[0.98] transition-transform" data-id="element-1351">
            Confirm
          </button>
        </div>
      </motion.div>
    </motion.div>;
}
function JobMapScreen({
  job,
  navStage,
  onClose
}: {
  job: DriverJob;
  navStage: 'pickup' | 'dropoff';
  onClose: () => void;
}) {
  const pickupHref = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.pickup)}`;
  const dropoffHref = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.dropoff)}`;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.18
  }} className="absolute inset-0 z-40 bg-white flex flex-col" data-id="element-1352">
      {/* Header */}
      <div className="shrink-0 bg-white px-5 pt-12 pb-3 border-b border-gray-100 z-10" data-id="element-1353">
        <div className="flex items-center justify-between mb-3" data-id="element-1354">
          <button onClick={onClose} className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Back" data-id="element-1355">
            <ChevronLeft className="w-5 h-5 text-gray-700" data-id="element-1356" />
          </button>
          <span className="text-[10px] text-gray-400" data-id="element-1357">{job.id}</span>
        </div>
        <h2 className="text-lg font-display font-bold text-gray-900" data-id="element-1358">
          Route map
        </h2>
        <p className="text-xs text-gray-500" data-id="element-1359">
          {navStage === 'pickup' ? 'Heading to pickup first' : 'Heading to dropoff next'}
        </p>
      </div>
      {/* Map + content */}
      <div className="flex-1 overflow-y-auto scrollbar-hide p-5 space-y-4 bg-gray-50" data-id="element-1360">
        <RoutePreviewMap pickup={job.pickup} dropoff={job.dropoff} distance="≈ 18 km" eta="≈ 24 min" data-id="element-1361" />
        <div className="bg-white border border-gray-100 shadow-sm rounded-2xl p-4 space-y-3" data-id="element-1362">
          <div className="flex items-start gap-2" data-id="element-1363">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${navStage === 'pickup' ? 'bg-brand-500 text-white' : 'bg-brand-50 text-brand-500'}`} data-id="element-1364">
              <MapPin className="w-3.5 h-3.5" data-id="element-1365" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-1366">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-1367">
                Pickup
              </p>
              <p className="text-sm text-gray-900" data-id="element-1368">{job.pickup}</p>
            </div>
          </div>
          <div className="flex items-start gap-2" data-id="element-1369">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${navStage === 'dropoff' ? 'bg-emerald-500 text-white' : 'bg-emerald-50 text-emerald-600'}`} data-id="element-1370">
              <MapPin className="w-3.5 h-3.5" data-id="element-1371" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-1372">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-1373">
                Dropoff
              </p>
              <p className="text-sm text-gray-900" data-id="element-1374">{job.dropoff}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Bottom CTAs */}
      <div className="shrink-0 bg-white border-t border-gray-100 px-5 pt-3 pb-7 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] z-10 space-y-2" data-id="element-1375">
        <a href={navStage === 'pickup' ? pickupHref : dropoffHref} target="_blank" rel="noopener noreferrer" className="w-full py-3.5 rounded-2xl orange-gradient-bg text-white font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-brand-500/30 active:scale-[0.98] transition-transform" data-id="element-1376">
          <Navigation className="w-4 h-4" data-id="element-1377" />
          Navigate to {navStage}
        </a>
        <a href={navStage === 'pickup' ? dropoffHref : pickupHref} target="_blank" rel="noopener noreferrer" className="w-full py-3 rounded-2xl bg-white border border-gray-200 text-gray-800 font-bold text-xs flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors" data-id="element-1378">
          <Navigation className="w-3.5 h-3.5" data-id="element-1379" />
          Navigate to {navStage === 'pickup' ? 'dropoff' : 'pickup'} instead
        </a>
      </div>
    </motion.div>;
}
// --- AUTH FLOW COMPONENTS ---
function DriverSignInFlow({
  stage,
  phone,
  onSetStage,
  onSetPhone,
  onComplete,
  onGuest
}: {
  stage: AuthStage;
  phone: string;
  onSetStage: (s: AuthStage) => void;
  onSetPhone: (p: string) => void;
  onComplete: (phone: string) => void;
  onGuest: () => void;
}) {
  return <div className="flex-1 relative overflow-hidden bg-white text-gray-900" data-id="element-1380">
      <AnimatePresence mode="wait" initial={false} data-id="element-1381">
        {stage === 'intro' && <motion.div key="intro" initial={{
        opacity: 1
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0,
        scale: 1.08,
        filter: 'blur(8px)'
      }} transition={{
        duration: 0.5,
        ease: [0.65, 0, 0.35, 1]
      }} className="absolute inset-0" data-id="element-1382">
            <DriverSplashIntro onComplete={() => onSetStage('splash')} data-id="element-1383" />
          </motion.div>}
        {stage === 'splash' && <motion.div key="splash" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.35
      }} className="absolute inset-0" data-id="element-1384">
            <DriverSignInSplash onStart={() => onSetStage('phone')} onGuest={onGuest} data-id="element-1385" />
          </motion.div>}
        {stage === 'phone' && <motion.div key="phone" initial={{
        opacity: 0,
        x: 30
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -30
      }} transition={{
        duration: 0.25
      }} className="absolute inset-0" data-id="element-1386">
            <DriverSignInPhone initialPhone={phone} onBack={() => onSetStage('splash')} onSubmit={p => {
          onSetPhone(p);
          onSetStage('otp');
        }} data-id="element-1387" />
          </motion.div>}
        {stage === 'otp' && <motion.div key="otp" initial={{
        opacity: 0,
        x: 30
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -30
      }} transition={{
        duration: 0.25
      }} className="absolute inset-0" data-id="element-1388">
            <DriverSignInOtp phone={phone} onBack={() => onSetStage('phone')} onResend={() => {}} onSubmit={() => onComplete(phone)} data-id="element-1389" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function DriverSplashIntro({
  onComplete
}: {
  onComplete: () => void;
}) {
  const reduce = useReducedMotion();
  const [phase, setPhase] = useState<0 | 1 | 2 | 3>(0);
  useEffect(() => {
    if (reduce) {
      const t = setTimeout(onComplete, 900);
      return () => clearTimeout(t);
    }
    const timers = [setTimeout(() => setPhase(1), 250), setTimeout(() => setPhase(2), 900), setTimeout(() => setPhase(3), 1700), setTimeout(() => onComplete(), 2900)];
    return () => timers.forEach(clearTimeout);
  }, [reduce, onComplete]);
  return <button type="button" onClick={onComplete} aria-label="Skip intro" className="relative w-full h-full overflow-hidden bg-surface-100 text-gray-900 block focus:outline-none" data-id="element-1390">
      <div className="absolute inset-0" style={{
      background: 'radial-gradient(circle at 50% 35%, rgba(241,245,249,1) 0%, rgba(248,250,252,1) 40%, #ffffff 100%)'
    }} data-id="element-1391" />
      <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-1392">
        <motion.div initial={{
        scale: 0.6,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: phase >= 1 ? 1 : 0
      }} transition={{
        duration: 0.6,
        ease: [0.65, 0, 0.35, 1]
      }} className="absolute w-72 h-72 rounded-full pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,107,53,0.22) 0%, rgba(255,107,53,0) 65%)'
      }} data-id="element-1393" />
        <motion.div initial={{
        scale: 0.7,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: phase >= 1 ? 1 : 0
      }} transition={{
        duration: 0.7,
        delay: 0.05
      }} className="absolute w-44 h-44 rounded-full border border-brand-200/70 pointer-events-none" data-id="element-1394" />
        <motion.div initial={{
        scale: 0.6,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: phase >= 2 ? 0.8 : 0
      }} transition={{
        duration: 0.6
      }} className="absolute w-64 h-64 rounded-full border border-brand-100 pointer-events-none" data-id="element-1395" />

        <motion.div initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: phase >= 3 ? 0.7 : 1,
        opacity: 1
      }} transition={{
        scale: {
          duration: 0.7,
          ease: [0.65, 0, 0.35, 1]
        },
        opacity: {
          duration: 0.4
        }
      }} className="relative w-24 h-24 rounded-3xl orange-gradient-bg flex items-center justify-center will-change-transform" style={{
        boxShadow: '0 20px 50px -10px rgba(255,107,53,0.55), 0 0 0 1px rgba(255,255,255,0.6) inset'
      }} data-id="element-1396">
          <motion.div initial={{
          scale: 0,
          rotate: -30
        }} animate={{
          scale: 1,
          rotate: 0
        }} transition={{
          delay: 0.35,
          type: 'spring',
          stiffness: 220,
          damping: 18
        }} data-id="element-1397">
            <Truck className="w-10 h-10 text-white" data-id="element-1398" />
          </motion.div>
        </motion.div>

        <AnimatePresence data-id="element-1399">
          {phase >= 3 && <motion.div key="wordmark" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.5,
          ease: [0.65, 0, 0.35, 1]
        }} className="absolute top-1/2 mt-20 text-center will-change-transform" data-id="element-1400">
              <h1 className="text-5xl font-display font-black tracking-tight leading-none text-gray-900" data-id="element-1401">
                PAIR<span className="orange-gradient-text" data-id="element-1402">2</span>
              </h1>
              <motion.div initial={{
            scaleX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            delay: 0.25,
            duration: 0.55,
            ease: 'easeOut'
          }} className="h-px w-28 mx-auto mt-3 bg-gradient-to-r from-transparent via-brand-400 to-transparent origin-center" data-id="element-1403" />
              <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.4,
            duration: 0.35
          }} className="mt-3 text-[10px] font-bold uppercase tracking-[0.38em] text-gray-500" data-id="element-1404">
                Drive&nbsp;·&nbsp;The&nbsp;·&nbsp;City
              </motion.p>
            </motion.div>}
        </AnimatePresence>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: -6
    }} animate={{
      opacity: phase >= 2 ? 1 : 0,
      y: 0
    }} transition={{
      duration: 0.35
    }} className="absolute top-7 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-gray-200 shadow-sm" data-id="element-1405">
        <span className="w-1 h-1 rounded-full bg-emerald-500" data-id="element-1406" />
        <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-gray-600" data-id="element-1407">
          Pair2 Driver
        </span>
      </motion.div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: phase >= 3 ? 1 : 0
    }} transition={{
      duration: 0.5
    }} className="absolute bottom-10 left-1/2 -translate-x-1/2 text-center" data-id="element-1408">
        <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-gray-400" data-id="element-1409">
          Tap to enter
        </p>
      </motion.div>
    </button>;
}
function DriverSignInSplash({
  onStart,
  onGuest
}: {
  onStart: () => void;
  onGuest: () => void;
}) {
  return <div className="relative w-full h-full flex flex-col items-center justify-between px-7 pt-10 pb-8 bg-surface-100 overflow-hidden" data-id="element-1410">
      <div className="pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(circle at 50% 20%, rgba(241,245,249,1) 0%, rgba(248,250,252,1) 35%, #ffffff 75%)'
    }} data-id="element-1411" />
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[460px] h-[460px] bg-slate-200/50 rounded-full blur-3xl" data-id="element-1412" />
      <div className="pointer-events-none absolute -bottom-40 -right-20 w-[340px] h-[340px] bg-blue-100/50 rounded-full blur-3xl" data-id="element-1413" />

      <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-gray-200 shadow-sm text-[10px] font-bold uppercase tracking-[0.18em] text-gray-700" data-id="element-1414">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1415" />
        Pair2 · Driver
      </motion.div>

      <div className="relative flex flex-col items-center" data-id="element-1416">
        <motion.div initial={{
        opacity: 0,
        scale: 0.6
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.15,
        duration: 0.55,
        ease: [0.65, 0, 0.35, 1]
      }} className="relative w-44 h-44 flex items-center justify-center mb-6" data-id="element-1417">
          <div className="absolute w-44 h-44 rounded-full bg-slate-100" data-id="element-1418" />
          <div className="absolute w-32 h-32 rounded-full bg-slate-200/60" data-id="element-1419" />
          <motion.div animate={{
          scale: [1, 1.04, 1]
        }} transition={{
          duration: 2.4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="relative w-24 h-24 rounded-3xl orange-gradient-bg flex items-center justify-center will-change-transform" style={{
          boxShadow: '0 18px 45px -10px rgba(255,107,53,0.55), 0 0 0 1px rgba(255,255,255,0.5) inset'
        }} data-id="element-1420">
            <Truck className="w-10 h-10 text-white" data-id="element-1421" />
          </motion.div>
        </motion.div>

        <motion.h1 initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35
      }} className="text-3xl font-display font-black text-center leading-tight text-gray-900" data-id="element-1422">
          Pick jobs.
          <br data-id="element-1423" />
          <span className="orange-gradient-text" data-id="element-1424">Get paid.</span>
        </motion.h1>
        <motion.p initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.45
      }} className="mt-3 text-center text-sm text-gray-600 max-w-[280px] leading-relaxed" data-id="element-1425">
          Drive the city with Pair2. Accept jobs, navigate easily, and track
          your earnings.
        </motion.p>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.55
    }} className="relative w-full space-y-2.5" data-id="element-1426">
        <button onClick={onStart} className="w-full py-4 orange-gradient-bg text-white rounded-2xl font-bold text-sm shadow-[0_12px_36px_-12px_rgba(255,107,53,0.55)] active:scale-[0.98] transition-transform flex items-center justify-center gap-2" data-id="element-1427">
          Get started
          <ArrowRight className="w-4 h-4" data-id="element-1428" />
        </button>
        <button onClick={onGuest} className="w-full py-3.5 bg-white hover:bg-gray-50 border border-gray-200 text-gray-800 rounded-2xl font-bold text-xs transition-colors" data-id="element-1429">
          Continue as demo driver
        </button>
        <p className="text-[10px] text-center text-gray-500 leading-snug pt-1" data-id="element-1430">
          By continuing you agree to our{' '}
          <span className="text-gray-700 underline-offset-2 underline" data-id="element-1431">
            Terms
          </span>{' '}
          &amp;{' '}
          <span className="text-gray-700 underline-offset-2 underline" data-id="element-1432">
            Privacy
          </span>
          .
        </p>
      </motion.div>
    </div>;
}
function DriverSignInPhone({
  initialPhone,
  onBack,
  onSubmit
}: {
  initialPhone: string;
  onBack: () => void;
  onSubmit: (phone: string) => void;
}) {
  const [country, setCountry] = useState('+971');
  const [number, setNumber] = useState(initialPhone.replace(/^\+\d+\s*/, '') || '');
  const isValid = number.replace(/\D/g, '').length >= 7;
  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit(`${country} ${number.trim()}`);
  };
  return <div className="relative w-full h-full flex flex-col bg-surface-100 text-gray-900 overflow-hidden" data-id="element-1433">
      <div className="pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(circle at 50% 0%, rgba(241,245,249,0.9) 0%, rgba(255,255,255,0) 55%)'
    }} data-id="element-1434" />
      <div className="relative px-5 pt-5 pb-2 flex items-center justify-between" data-id="element-1435">
        <button onClick={onBack} className="w-9 h-9 rounded-full bg-white border border-gray-200 hover:bg-gray-50 flex items-center justify-center transition-colors text-gray-700" aria-label="Back" data-id="element-1436">
          <ChevronLeft className="w-4 h-4" data-id="element-1437" />
        </button>
        <div className="flex items-center gap-1" data-id="element-1438">
          {[0, 1, 2].map(i => <div key={i} className={`h-1 rounded-full transition-all ${i === 0 ? 'w-6 bg-brand-500' : 'w-1.5 bg-gray-200'}`} data-id="element-1439" />)}
        </div>
        <div className="w-9" data-id="element-1440" />
      </div>

      <div className="flex-1 px-7 pt-8 flex flex-col items-center text-center" data-id="element-1441">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-600 mb-2" data-id="element-1442">
          Step 1 of 2
        </p>
        <h1 className="text-2xl font-display font-bold leading-tight text-gray-900" data-id="element-1443">
          What's your{' '}
          <span className="orange-gradient-text" data-id="element-1444">mobile number?</span>
        </h1>
        <p className="text-sm text-gray-500 mt-2 leading-relaxed max-w-[260px]" data-id="element-1445">
          We'll text a 6-digit code to verify it. Standard rates may apply.
        </p>

        <div className="mt-8 w-full flex gap-2" data-id="element-1446">
          <div className="relative shrink-0" data-id="element-1447">
            <select value={country} onChange={e => setCountry(e.target.value)} className="appearance-none bg-white border border-gray-200 text-gray-900 text-sm font-bold rounded-xl pl-3 pr-7 py-3.5 focus:outline-none focus:border-brand-400 transition-colors shadow-sm" data-id="element-1448">
              <option value="+971" data-id="element-1449">🇦🇪 +971</option>
              <option value="+966" data-id="element-1450">🇸🇦 +966</option>
              <option value="+91" data-id="element-1451">🇮🇳 +91</option>
              <option value="+44" data-id="element-1452">🇬🇧 +44</option>
              <option value="+1" data-id="element-1453">🇺🇸 +1</option>
            </select>
            <ChevronRight className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 rotate-90 pointer-events-none" data-id="element-1454" />
          </div>
          <input type="tel" inputMode="numeric" placeholder="50 123 4567" value={number} onChange={e => setNumber(e.target.value.replace(/[^\d\s]/g, ''))} onKeyDown={e => e.key === 'Enter' && handleSubmit()} autoFocus className="flex-1 min-w-0 bg-white border border-gray-200 text-gray-900 text-base font-semibold tabular-nums rounded-xl px-4 py-3.5 text-center focus:outline-none focus:border-brand-400 placeholder:text-gray-400 transition-colors shadow-sm" data-id="element-1455" />
        </div>

        <div className="mt-6 flex items-start gap-2 text-[11px] text-gray-500 text-left max-w-[280px]" data-id="element-1456">
          <Shield className="w-3.5 h-3.5 mt-0.5 text-emerald-500 shrink-0" data-id="element-1457" />
          <p className="leading-snug" data-id="element-1458">
            Your number is encrypted end-to-end and never shared with customers
            until you accept a job.
          </p>
        </div>
      </div>

      <div className="relative px-5 pb-7 pt-3" data-id="element-1459">
        <button onClick={handleSubmit} disabled={!isValid} className="w-full py-4 orange-gradient-bg text-white rounded-2xl font-bold text-sm shadow-[0_12px_36px_-12px_rgba(255,107,53,0.55)] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:shadow-none" data-id="element-1460">
          Send code
          <ArrowRight className="w-4 h-4" data-id="element-1461" />
        </button>
      </div>
    </div>;
}
function DriverSignInOtp({
  phone,
  onBack,
  onResend,
  onSubmit
}: {
  phone: string;
  onBack: () => void;
  onResend: () => void;
  onSubmit: () => void;
}) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(30);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);
  const allFilled = code.every(c => c !== '');
  useEffect(() => {
    if (!allFilled) return;
    setVerifying(true);
    const t = setTimeout(() => {
      onSubmitRef.current();
    }, 900);
    return () => clearTimeout(t);
  }, [allFilled]);
  const setDigit = (idx: number, val: string) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    setCode(c => {
      const next = [...c];
      next[idx] = digit;
      return next;
    });
    if (digit && idx < 5) {
      inputsRef.current[idx + 1]?.focus();
    }
  };
  const handleKeyDown = (idx: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };
  const handleResend = () => {
    setResendIn(30);
    setCode(['', '', '', '', '', '']);
    inputsRef.current[0]?.focus();
    onResend();
  };
  return <div className="relative w-full h-full flex flex-col bg-surface-100 text-gray-900 overflow-hidden" data-id="element-1462">
      <div className="pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(circle at 50% 0%, rgba(241,245,249,0.9) 0%, rgba(255,255,255,0) 55%)'
    }} data-id="element-1463" />
      <div className="relative px-5 pt-5 pb-2 flex items-center justify-between" data-id="element-1464">
        <button onClick={onBack} className="w-9 h-9 rounded-full bg-white border border-gray-200 hover:bg-gray-50 flex items-center justify-center transition-colors text-gray-700" aria-label="Back" data-id="element-1465">
          <ChevronLeft className="w-4 h-4" data-id="element-1466" />
        </button>
        <div className="flex items-center gap-1" data-id="element-1467">
          {[0, 1, 2].map(i => <div key={i} className={`h-1 rounded-full transition-all ${i <= 1 ? 'w-6 bg-brand-500' : 'w-1.5 bg-gray-200'}`} data-id="element-1468" />)}
        </div>
        <div className="w-9" data-id="element-1469" />
      </div>
      <div className="flex-1 px-7 pt-8 flex flex-col items-center text-center" data-id="element-1470">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-600 mb-2" data-id="element-1471">
          Step 2 of 2
        </p>
        <h1 className="text-2xl font-display font-bold leading-tight text-gray-900" data-id="element-1472">
          Enter the <span className="orange-gradient-text" data-id="element-1473">6-digit code</span>
        </h1>
        <p className="text-sm text-gray-500 mt-2 leading-relaxed max-w-[280px]" data-id="element-1474">
          Sent to <span className="text-gray-900 font-semibold" data-id="element-1475">{phone}</span>.{' '}
          <button onClick={onBack} className="text-brand-600 hover:text-brand-700 underline-offset-2 underline" data-id="element-1476">
            Wrong number?
          </button>
        </p>

        <div className="mt-8 w-full flex gap-2 justify-between" data-id="element-1477">
          {code.map((digit, idx) => <input key={idx} ref={el => {
          inputsRef.current[idx] = el;
        }} value={digit} onChange={e => setDigit(idx, e.target.value)} onKeyDown={e => handleKeyDown(idx, e)} inputMode="numeric" maxLength={1} disabled={verifying} autoFocus={idx === 0} className={`w-11 h-14 text-center text-xl font-display font-bold tabular-nums bg-white border-2 rounded-xl focus:outline-none transition-colors shadow-sm ${digit ? 'border-brand-400 text-gray-900' : 'border-gray-200 text-gray-400'} ${verifying ? 'opacity-60' : ''}`} data-id="element-1478" />)}
        </div>

        {verifying ? <div className="mt-6 flex items-center justify-center gap-2 text-sm text-emerald-600 font-semibold" data-id="element-1479">
            <Loader2 className="w-4 h-4 animate-spin" data-id="element-1480" />
            Verifying…
          </div> : <div className="mt-6 text-center text-xs text-gray-500" data-id="element-1481">
            {resendIn > 0 ? <span data-id="element-1482">
                Didn't get it? Resend in{' '}
                <span className="text-gray-900 font-semibold tabular-nums" data-id="element-1483">
                  {resendIn}s
                </span>
              </span> : <button onClick={handleResend} className="text-brand-600 hover:text-brand-700 font-semibold" data-id="element-1484">
                Resend code
              </button>}
          </div>}
      </div>
      <div className="relative px-5 pb-7 pt-3" data-id="element-1485">
        <div className="flex items-start gap-2 text-[10px] text-gray-500 mb-3 max-w-[280px] mx-auto" data-id="element-1486">
          <Shield className="w-3 h-3 mt-0.5 text-emerald-500 shrink-0" data-id="element-1487" />
          <p className="leading-snug" data-id="element-1488">
            For your security, the code expires in 5 minutes.
          </p>
        </div>
      </div>
    </div>;
}